import React, { useState, useEffect } from 'react';
import { clientsApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Plus, Search, Pencil, Trash2, Users, Tag } from 'lucide-react';
import ClientPricesDialog from '../components/ClientPricesDialog';
import ClientEinvoiceFields from '../components/ClientEinvoiceFields';
import { toast } from 'sonner';

const emptyClient = {
  name: '',
  nickname: '',
  address: '',
  city: '',
  cap: '',
  piva: '',
  email: '',
  phone: '',
  codice_fiscale: '',
  provincia: '',
  codice_destinatario: '',
  pec: ''
};

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [formData, setFormData] = useState(emptyClient);
  const [saving, setSaving] = useState(false);
  const [pricesClient, setPricesClient] = useState(null);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async (searchTerm = '') => {
    try {
      const response = await clientsApi.getAll(searchTerm);
      setClients(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei clienti');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    fetchClients(value);
  };

  const openCreateDialog = () => {
    setSelectedClient(null);
    setFormData(emptyClient);
    setDialogOpen(true);
  };

  const openEditDialog = (client) => {
    setSelectedClient(client);
    setFormData({
      name: client.name,
      nickname: client.nickname || '',
      address: client.address,
      city: client.city,
      cap: client.cap,
      piva: client.piva,
      email: client.email,
      phone: client.phone,
      codice_fiscale: client.codice_fiscale || '',
      provincia: client.provincia || '',
      codice_destinatario: client.codice_destinatario || '',
      pec: client.pec || ''
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (client) => {
    setSelectedClient(client);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async (e, confirmDuplicate = false) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!formData.name) {
      toast.error('Il nome è obbligatorio');
      return;
    }

    setSaving(true);
    try {
      if (selectedClient) {
        await clientsApi.update(selectedClient.id, formData, confirmDuplicate);
        toast.success('Cliente aggiornato');
      } else {
        await clientsApi.create(formData, confirmDuplicate);
        toast.success('Cliente creato');
      }
      setDialogOpen(false);
      fetchClients(search);
    } catch (error) {
      const detail = error.response?.data?.detail;
      // P.IVA duplicata: chiedi conferma
      if (error.response?.status === 409 && detail?.code === 'DUPLICATE_PIVA') {
        const ec = detail.existing_client || {};
        const msg = `${detail.message}\n\nCliente esistente:\n• Nome: ${ec.name || '—'}\n• Indirizzo: ${ec.address || '—'} ${ec.city || ''}\n\nVuoi crearlo comunque (negozio diverso con stessa P.IVA)?`;
        if (window.confirm(msg)) {
          await handleSubmit(null, true);
          return;
        }
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Errore nel salvataggio');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await clientsApi.delete(selectedClient.id);
      toast.success('Cliente eliminato');
      setDeleteDialogOpen(false);
      fetchClients(search);
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  return (
    <div data-testid="clients-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-['Manrope']">Clienti</h1>
          <p className="text-muted-foreground text-sm">Gestisci l'anagrafica clienti</p>
        </div>
        <Button data-testid="new-client-btn" onClick={openCreateDialog} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          NUOVO CLIENTE
        </Button>
      </div>

      {/* Search */}
      <Card className="card-swiss">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              data-testid="search-clients-input"
              placeholder="Cerca per nome (come lo conosci)..."
              value={search}
              onChange={handleSearch}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="card-swiss">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-muted-foreground">Caricamento...</div>
            </div>
          ) : clients.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="table-header-swiss">Nome (come lo conosci)</TableHead>
                  <TableHead className="table-header-swiss">Ragione Sociale</TableHead>
                  <TableHead className="table-header-swiss">Indirizzo</TableHead>
                  <TableHead className="table-header-swiss">P.IVA</TableHead>
                  <TableHead className="table-header-swiss">Contatti</TableHead>
                  <TableHead className="table-header-swiss w-[100px]">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map((client) => {
                  const display = (client.nickname && client.nickname.trim()) ? client.nickname : client.name;
                  const showRagSoc = client.nickname && client.nickname.trim() && client.nickname !== client.name;
                  return (
                  <TableRow key={client.id} className="table-row-swiss">
                    <TableCell className="table-cell-swiss font-semibold text-base">{display}</TableCell>
                    <TableCell className="table-cell-swiss text-sm text-muted-foreground">
                      {showRagSoc ? client.name : <span className="italic">—</span>}
                    </TableCell>
                    <TableCell className="table-cell-swiss text-muted-foreground text-sm">
                      {client.address}{client.city && `, ${client.city}`}{client.cap && ` ${client.cap}`}
                    </TableCell>
                    <TableCell className="table-cell-swiss font-['JetBrains_Mono'] text-sm">
                      {client.piva || '-'}
                    </TableCell>
                    <TableCell className="table-cell-swiss text-muted-foreground text-xs">
                      {client.email && <div>{client.email}</div>}
                      {client.phone && <div>{client.phone}</div>}
                      {!client.email && !client.phone && '-'}
                    </TableCell>
                    <TableCell className="table-cell-swiss">
                      <div className="flex gap-1">
                        <Button
                          data-testid={`edit-client-${client.id}`}
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(client)}
                          className="h-8 w-8"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`client-prices-${client.id}`}
                          variant="ghost"
                          size="icon"
                          title="Prezzi memorizzati"
                          onClick={() => setPricesClient(client)}
                          className="h-8 w-8"
                        >
                          <Tag className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );})}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nessun cliente trovato</p>
              <Button onClick={openCreateDialog} className="mt-4 btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                AGGIUNGI IL PRIMO CLIENTE
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <ClientPricesDialog
        client={pricesClient}
        open={!!pricesClient}
        onOpenChange={(o) => { if (!o) setPricesClient(null); }}
      />

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] font-semibold">
              {selectedClient ? 'Modifica Cliente' : 'Nuovo Cliente'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label className="label-uppercase">Nome (come lo conosci) *</Label>
                <Input
                  data-testid="client-nickname-input"
                  value={formData.nickname}
                  onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                  placeholder="Es. Macelleria Youssef"
                  className="text-base font-semibold"
                />
                <p className="text-[11px] text-muted-foreground">È il nome con cui cercherai questo cliente nelle bolle e nei filtri.</p>
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase">Ragione Sociale *</Label>
                <Input
                  data-testid="client-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Es. Brahim Mohamed Rimini"
                />
                <p className="text-[11px] text-muted-foreground">Intestatario formale (P.IVA). Apparirà sui PDF/DDT.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-uppercase">Indirizzo</Label>
                  <Input
                    data-testid="client-address-input"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Via Roma, 1"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">Città</Label>
                  <Input
                    data-testid="client-city-input"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Milano"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-uppercase">CAP</Label>
                  <Input
                    data-testid="client-cap-input"
                    value={formData.cap}
                    onChange={(e) => setFormData({ ...formData, cap: e.target.value })}
                    placeholder="20100"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">P.IVA</Label>
                  <Input
                    data-testid="client-piva-input"
                    value={formData.piva}
                    onChange={(e) => setFormData({ ...formData, piva: e.target.value })}
                    placeholder="IT00000000000"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-uppercase">Email</Label>
                  <Input
                    data-testid="client-email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@esempio.it"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">Telefono</Label>
                  <Input
                    data-testid="client-phone-input"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+39 000 0000000"
                  />
                </div>
              </div>
              <ClientEinvoiceFields formData={formData} setFormData={setFormData} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button data-testid="save-client-btn" type="submit" disabled={saving} className="btn-primary">
                {saving ? 'Salvataggio...' : 'SALVA'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare il cliente "{selectedClient?.name}"? Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-client-btn"
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ClientsPage;
